import { useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type UploadState = "idle" | "uploading" | "success" | "error";

interface UploadResult {
  total_rows: number;
  inserted: number;
  errors: number;
  message: string;
}

export const CsvUploadReglasCard = () => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<UploadState>("idle");
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState<UploadResult | null>(null);

  const uploadFile = useCallback(async (file: File) => {
    if (!file.name.endsWith(".csv")) {
      toast({ title: "Formato inválido", description: "Solo se permiten archivos .csv", variant: "destructive" });
      return;
    }

    setState("uploading");
    setFileName(file.name);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No autenticado");

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bulk-upload-reglas`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        }
      );

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Error al procesar");

      setResult(data);
      setState("success");
      toast({ title: "✅ Carga completada", description: data.message });
    } catch (err: any) {
      setState("error");
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  }, [toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  };

  const reset = () => {
    setState("idle");
    setFileName("");
    setResult(null);
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Carga Masiva — Reglas de Cumplimiento
        </CardTitle>
        <CardDescription>Sube un CSV para actualizar reglas de cumplimiento (UPSERT por codigo_partida)</CardDescription>
      </CardHeader>
      <CardContent>
        {state === "idle" && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            }`}
          >
            <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">Arrastra tu archivo CSV aquí</p>
            <p className="text-xs text-muted-foreground mt-1">o haz clic para seleccionar</p>
            <p className="text-xs text-muted-foreground mt-3">
              Columnas: codigo_partida, entidad, tipo_tramite, descripcion_regla, nivel_riesgo, nota_marginal_exclusiones, norma
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        )}

        {state === "uploading" && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-primary animate-pulse" />
              <span className="text-sm text-foreground font-medium">{fileName}</span>
            </div>
            <Progress value={undefined} className="h-2" />
            <p className="text-xs text-muted-foreground">Procesando reglas en lotes...</p>
          </div>
        )}

        {state === "success" && result && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium text-foreground">{result.message}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-lg font-bold text-foreground">{result.total_rows}</p>
                <p className="text-xs text-muted-foreground">Total filas</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-lg font-bold text-foreground">{result.inserted}</p>
                <p className="text-xs text-muted-foreground">Insertados</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-lg font-bold text-destructive">{result.errors}</p>
                <p className="text-xs text-muted-foreground">Errores</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={reset} className="w-full">
              Subir otro archivo
            </Button>
          </div>
        )}

        {state === "error" && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <span className="text-sm font-medium text-foreground">Error al procesar {fileName}</span>
            </div>
            <Button variant="outline" size="sm" onClick={reset} className="w-full">
              Reintentar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
